import React, { useState, useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { announceMedals } from "./medalEvents";
import "./btAudio";   // débloque les lecteurs du Blind Test au premier geste
import { socket } from "./socket";
import { v4 as uuidv4 } from 'uuid';

import 'bootstrap-icons/font/bootstrap-icons.css';
import "./styles/layout.css";
// Les thèmes cohabitent : chacun est préfixé par son attribut, seul celui posé
// sur <html> s'applique (voir theme.js).
import "./themes/popuquiz.css";
import "./themes/popuwatch.css";
import "./themes/neon.css";
import "./themes/frutiger.css";
import "./themes/gundam.css";
import "./themes/backrooms.css";
import "./themes/seventies.css";
import "./themes/eighties.css";
import "./themes/noel.css";
import "./themes/piscine.css";
import "./themes/minecraft.css";
import "./themes/stardew.css";
import "./themes/splatoon.css";
import "./themes/crochet.css";
import "./styles/cosmetics.css";
import "./theme";

import { ROUTES, useRoute, navigate, goBack, currentPath, gameIdFromPath } from "./router";
import { t, useLanguage } from "./i18n";

import Navigation from "./components/Navigation";
import Welcome from "./components/Welcome";
import Setup from "./components/Setup";
import Loading from "./components/Loading";
import Auth from "./components/Auth";
import Profile from "./components/Profile";
import Lobby from "./components/Lobby";
import GameLobby from "./components/GameLobby";
import Quiz from "./components/Quiz";
import BlindTest from "./components/BlindTest";
import CacophonieBlindTest from "./components/CacophonieBlindTest";
import ImposterQuiz from "./components/ImposterQuiz";
import BattleRoyale from "./components/BattleRoyale";
import BombGame from "./components/BombGame";
import PixelGuess from "./components/PixelGuess";
import SmashPass from "./components/SmashPass";
import References from "./components/References";
import PictionaryGame from "./components/PictionaryGame";
import HemicycleGame from "./components/HemicycleGame";
import UndercoverGame from "./components/UndercoverGame";
import ParolesGame from "./components/ParolesGame";
import BuzzerQuiz from "./components/BuzzerQuiz";
import FinalScores from "./components/FinalScores";
import TieBreak from "./components/TieBreak";
import Shop from "./components/Shop";
import Settings from "./components/Settings";
import PlayerStats from "./components/PlayerStats";
import MedalsPage from "./components/MedalsPage";
import Results from "./components/Results";
import Admin from "./components/Admin";
import { confirmDialog, alertDialog, choiceDialog } from "./dialog";
import { useFeatures } from "./features";


const API = process.env.REACT_APP_API_URL || "";

// Sans photo, l'avatar est dessiné à partir du pseudo (initiales sur fond
// coloré) : rien à servir par défaut, et l'image reste un objet de boutique.

function getOrCreatePlayerId() {
  let id = localStorage.getItem("popuquiz_playerId");
  if (!id) {
    id = uuidv4();
    localStorage.setItem("popuquiz_playerId", id);
  }
  return id;
}

// Persistent player account ({ token, username, role }) — null when logged out (anonymous)
function getSavedAccount() {
  try { return JSON.parse(localStorage.getItem("popuquiz_account")) || null; }
  catch (_) { return null; }
}

// Identité de jeu (nom affiché + photo). Sa source depend du statut :
//  • anonyme  -> le navigateur, seul endroit ou elle existe ;
//  • connecte -> le compte sur le serveur, pour suivre d'un appareil a l'autre.
// Rien n'est ecrit en local tant qu'un compte est ouvert : au prochain
// chargement, c'est le serveur qui redit qui l'on est.
const IDENTITY_KEYS = ["popuquiz_name", "popuquiz_picture"];

function readLocalIdentity() {
  return {
    pseudo: localStorage.getItem("popuquiz_name") || "",
    picture: localStorage.getItem("popuquiz_picture") || "",
  };
}
function clearLocalIdentity() {
  IDENTITY_KEYS.forEach(k => localStorage.removeItem(k));
}
// `logged` vrai = compte ouvert : on ne laisse aucune trace dans le navigateur.
function writeLocalIdentity({ pseudo, picture }, logged) {
  if (logged) return clearLocalIdentity();
  localStorage.setItem("popuquiz_name", pseudo || "");
  localStorage.setItem("popuquiz_picture", picture || "");
}

// L'identite courante, hors React : les rappels de socket en ont besoin sans
// pouvoir lire l'etat du composant.
let currentIdentity = readLocalIdentity();

// Identity sent to the server on every game creation / join
function playerPayload() {
  return {
    pseudo: currentIdentity.pseudo,
    picture: currentIdentity.picture,
    playerId: getOrCreatePlayerId(),
    token: getSavedAccount()?.token || null,
  };
}

function App() {
  const features = useFeatures();
  // Re-renders the whole tree when the language changes (nothing is memoised,
  // so this single subscription is enough for every screen below).
  useLanguage();

  const [role, setRole] = useState("");
  // Compte ouvert : l'identite viendra du serveur, on ne prend rien en local.
  const bootAccount = getSavedAccount();
  const bootIdentity = bootAccount ? { pseudo: "", picture: "" } : readLocalIdentity();
  const [name, setName] = useState(bootIdentity.pseudo);
  const [picture, setPicture] = useState(bootIdentity.picture);

  const [players, setPlayers] = useState([]);
  const [player, setPlayer] = useState(() => (
    bootIdentity.pseudo
      ? { pseudo: bootIdentity.pseudo, picture: bootIdentity.picture, role: "", playerId: getOrCreatePlayerId(), token: bootAccount?.token || null }
      : {}
  ));
  // Tant que le serveur n'a pas rendu l'identite du compte, on n'affiche pas
  // l'accueil : sinon l'ecran « Pret a jouer ? » clignote au rechargement.
  const [identityLoading, setIdentityLoading] = useState(!!bootAccount);
  // Droits d'envoi achetés en boutique : ils commandent le bouton photo et les
  // formats que le sélecteur de fichier accepte.
  const [avatarRights, setAvatarRights] = useState({ image: false, gif: false });
  // Un compte venu du SSO n'a pas de mot de passe local à changer.
  const [hasPassword, setHasPassword] = useState(true);
  // Habillages achetés : les réglages ne proposent que ceux-là.
  const [ownedThemes, setOwnedThemes] = useState([]);
  // Palettes achetées : même principe, pour les thèmes qui en proposent plusieurs.
  const [ownedPalettes, setOwnedPalettes] = useState([]);

  const [gameId, setGameId] = useState("");

  // The URL is the source of truth for the displayed screen; /partie/:code names the game.
  const path = useRoute();
  const routeGameId = gameIdFromPath(path);
  const joinAskedRef = useRef(null); // last code requested from the server (loop guard)

  // Game targeted by an invite link opened without an identity: we join it once
  // the profile has been set up.
  const [pendingGameId, setPendingGameId] = useState(null);

  const [account, setAccount] = useState(getSavedAccount);
  const [authMode, setAuthMode] = useState("login"); // onglet ouvert sur /connexion

  // Instance neuve : tant qu'aucun superadmin n'existe, rien d'autre ne s'ouvre.
  // null = on ne sait pas encore (le serveur n'a pas répondu).
  const [needsSetup, setNeedsSetup] = useState(null);

  const [started, setStarted] = useState(false);
  const [gameMode, setGameMode] = useState("culture");
  const [btVariant, setBtVariant] = useState("classique");
  const [rfMode, setRfMode] = useState(null);   // Références : détente ou compétitif
  const [showResults, setShowResults] = useState(false);
  const [validationView, setValidationView] = useState(null);
  const [imposterRoles, setImposterRoles] = useState(null); // question index -> imposter playerId
  const [showScores, setShowScores] = useState(false);
  const [tieBreak, setTieBreak] = useState(null);   // tie-break duel in progress

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState({});

  // Leave the game screen: the game no longer exists (or we left it)
  const resetGameState = () => {
    localStorage.removeItem("popuquiz_gameId");
    joinAskedRef.current = null;
    setRole("");
    setGameId("");
    setStarted(false);
    setShowResults(false);
    setShowScores(false);
    setTieBreak(null);
    setPlayers([]);
  };

  useEffect(() => {

    /* Step 1: the player chooses to create or join a game */
    socket.on("gameCreated", (gameId) => {
      localStorage.setItem("popuquiz_gameId", gameId);
      joinAskedRef.current = gameId;
      setGameId(gameId);
      setRole("host");
      navigate(ROUTES.game(gameId));
    });
    // No navigation here: the URL is what takes you into a game, not the other
    // way round (a silent reconnection must not move the player).
    socket.on("gameJoined", ({ gameId, isHost, midGame }) => {
      localStorage.setItem("popuquiz_gameId", gameId);
      setGameId(gameId);
      setRole(isHost ? "host" : "player");
      // Arrivée en cours de partie : on prévient, sans bloquer.
      if (midGame) alertDialog(t("game.joinedMidGame"));
    });
    // Join failures: only clear what relates to the targeted game (the player
    // may well still be in another one).
    const joinFailed = (failedId) => {
      if (localStorage.getItem("popuquiz_gameId") === failedId) localStorage.removeItem("popuquiz_gameId");
      if (joinAskedRef.current === failedId) joinAskedRef.current = null;
      if (gameIdFromPath(currentPath()) === failedId) navigate(ROUTES.home, { replace: true });
    };
    socket.on("gameNotFound", ({ gameId }) => {
      const onIt = gameIdFromPath(currentPath()) === gameId;
      joinFailed(gameId);
      if (onIt) alertDialog(t("game.notFound"));
    });
    // Bombe, Battle Royale, Imposteur : on ne rejoint pas une partie lancée.
    socket.on("gameInProgress", ({ gameId }) => {
      joinFailed(gameId);
      alertDialog(t("game.inProgressLocked"));
    });
    // Partie en pause déjà rouverte par un autre participant : on la rejoint.
    socket.on("resumeJoin", ({ gameId }) => navigate(ROUTES.game(gameId)));
    socket.on("resumeError", ({ error }) => alertDialog(error));
    socket.on("gameFull", ({ gameId, maxPlayers }) => {
      joinFailed(gameId);
      alertDialog(t("game.full", { count: maxPlayers }));
    });
    socket.on("gameStopped", () => {
      resetGameState();
      if (gameIdFromPath(currentPath())) navigate(ROUTES.home, { replace: true });
    });
    socket.on("leftGame", () => {
      resetGameState();
      if (gameIdFromPath(currentPath())) navigate(ROUTES.home, { replace: true });
    });
    socket.on("updatePlayers", (players) => setPlayers(players));

    /* Step 2: the game has started */
    socket.on("startedQuiz", (data) => {
      setGameMode(typeof data === "object" && data?.gameMode ? data.gameMode : "culture");
      setRfMode(typeof data === "object" && data?.rfMode ? data.rfMode : null);
      if (typeof data === "object" && data?.btVariant) setBtVariant(data.btVariant);
      // A restart at the end of a game resumes here: clear the scores screen
      setShowScores(false);
      setShowResults(false);
      setTieBreak(null);
      setStarted(true);
    });

    /* The host left: another player is given the role */
    socket.on("hostGranted", () => setRole("host"));

    /* Retour au salon décidé par le joueur lui-même : la manche continue sans
       lui, on repasse simplement sur l'écran du salon. */
    socket.on("waitingInLobby", () => {
      setStarted(false);
      setShowResults(false);
      setTieBreak(null);
    });

    /* End of game: the host brings everyone back to the lobby to reconfigure */
    socket.on("returnedToLobby", () => {
      setStarted(false);
      setShowResults(false);
      setShowScores(false);
      setTieBreak(null);
      setResults({});
      setQuestions([]);
      setAnswers({});
      setValidationView(null);
      setImposterRoles(null);
    });

    /* Step 3: the game has finished, we show the results (depending on game type) */

    /* Type 1: Culture */
    socket.on("startValidation", (data) => {
      setStarted(false);
      setPlayers(data.players);
      setQuestions(data.questions);
      setAnswers(data.answers);
      setValidationView(data.validationView);
      setImposterRoles(data.imposterRoles || null);
      setShowResults(true);
    });
    socket.on("updateValidationView", ({ validationView }) => {
      setValidationView(validationView);
    });

    /* Type 2: Blind Test */
    /* Type 3: General */

    socket.on("gameEnded", (data) => {
      setResults(data);
      setShowResults(false);
      setTieBreak(null);
      setShowScores(true);
    });

    /* Tie at the top: tie-break duel before the results */
    socket.on("tieBreakStart", (data) => {
      setShowResults(false);
      setStarted(false);
      setTieBreak(data);
    });

    /* Fin d'un Buzzer : pas de validation, scores directs */
    socket.on("bzGameEnd", (data) => {
      setResults(data);
      setStarted(false);
      setShowResults(false);
      setShowScores(true);
    });

    /* Fin d'un Battle Royale : pas de validation, scores directs */
    socket.on("brGameEnd", (data) => {
      setResults(data);
      setStarted(false);
      setShowResults(false);
      setShowScores(true);
    });

    /* Fin d'un Pictionary : pas de validation, scores directs */
    socket.on("piGameEnd", (data) => {
      setResults(data);
      setStarted(false);
      setShowResults(false);
      setShowScores(true);
    });

    /* Fin d'un Undercover : pas de validation, scores directs */
    socket.on("ucGameEnd", (data) => {
      setResults(data);
      setStarted(false);
      setShowResults(false);
      setShowScores(true);
    });

    /* Fin d'un Hémicycle : pas de validation, scores directs */
    socket.on("hmGameEnd", (data) => {
      setResults(data);
      setStarted(false);
      setShowResults(false);
      setShowScores(true);
    });

    /* Fin d'un mur de Références : pas de validation, scores directs */
    socket.on("rfGameEnd", (data) => {
      setResults(data);
      setStarted(false);
      setShowResults(false);
      setShowScores(true);
    });

    /* Fin d'un Pixel Guess : pas de validation, scores directs */
    socket.on("pgGameEnd", (data) => {
      setResults(data);
      setStarted(false);
      setShowResults(false);
      setShowScores(true);
    });

    /* Fin d'une Bombe : pas de validation, scores directs */
    socket.on("boGameEnd", (data) => {
      setResults(data);
      setStarted(false);
      setShowResults(false);
      setShowScores(true);
    });

    /* End of a Blind Test game: no validation, straight to the scores */
    socket.on("btGameEnd", (data) => {
      setResults(data);
      setStarted(false);
      setShowResults(false);
      setShowScores(true);
    });

    // Silent reconnection: a game was in progress and the URL names none (a
    // game address is handled by the route effect instead).
    const savedGameId = localStorage.getItem("popuquiz_gameId");
    if (savedGameId && currentIdentity.pseudo && !gameIdFromPath(currentPath())) {
      socket.emit("joinGame", { gameId: savedGameId, player: playerPayload() });
    }

    return () => {
      socket.off("startedQuiz");
      socket.off("gameJoined");
      socket.off("gameNotFound");
      socket.off("gameFull");
      socket.off("gameInProgress");
      socket.off("resumeJoin");
      socket.off("resumeError");
      socket.off("gameStopped");
      socket.off("leftGame");
      socket.off("updatePlayers");
      socket.off("startValidation");
      socket.off("updateValidationView");
      socket.off("gameEnded");
      socket.off("tieBreakStart");
      socket.off("btGameEnd");
      socket.off("brGameEnd");
      socket.off("bzGameEnd");
      socket.off("boGameEnd");
      socket.off("pgGameEnd");
      socket.off("rfGameEnd");
      socket.off("piGameEnd");
      socket.off("hmGameEnd");
      socket.off("ucGameEnd");
      socket.off("gameCreated");
      socket.off("hostGranted");
      socket.off("waitingInLobby");
      socket.off("returnedToLobby");
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Le code Konami : personne d'autre que le navigateur ne le voit passer,
  // c'est donc lui qui va le raconter au serveur. Une médaille, rien de plus.
  useEffect(() => {
    if (!account?.token) return undefined;
    const code = ["ArrowUp", "ArrowUp", "ArrowDown", "ArrowDown", "ArrowLeft", "ArrowRight", "ArrowLeft", "ArrowRight", "b", "a"];
    let step = 0;
    const onKey = (e) => {
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      step = key === code[step] ? step + 1 : (key === code[0] ? 1 : 0);
      if (step < code.length) return;
      step = 0;
      fetch(`${API}/auth/me/secret`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${account.token}` },
        body: JSON.stringify({ id: "konami" }),
      }).then(r => r.json()).then(d => announceMedals(d.medals)).catch(() => {});
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [account?.token]);

  // Miroir hors React de l'identite : les rappels de socket la lisent sans
  // pouvoir atteindre l'etat du composant.
  useEffect(() => {
    currentIdentity = { pseudo: player.pseudo || "", picture: player.picture || "" };
  }, [player.pseudo, player.picture]);

  // Compte ouvert : c'est le serveur qui dit qui l'on est, a chaque chargement.
  // Une photo changee sur un autre appareil arrive donc au rafraichissement.
  useEffect(() => {
    if (!account?.token) { setIdentityLoading(false); return; }
    let cancelled = false;
    fetch(`${API}/auth/me/profile`, { headers: { Authorization: `Bearer ${account.token}` } })
      .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(d => {
        if (cancelled) return;
        clearLocalIdentity();          // connecte : rien ne reste dans le navigateur
        if (d.pseudo) {
          setName(d.pseudo);
          setPicture(d.picture || "");
          setPlayer(p => ({
            ...(p || {}), pseudo: d.pseudo, picture: d.picture || "",
            playerId: getOrCreatePlayerId(), token: account.token,
            cosmetics: d.cosmetics || null,
          }));
        }
        setAvatarRights(d.avatarUploads || { image: false, gif: false });
        setOwnedThemes(d.themes || []);
        setOwnedPalettes(d.palettes || []);
        setHasPassword(d.hasPassword !== false);
        setIdentityLoading(false);
      })
      .catch(() => { if (!cancelled) setIdentityLoading(false); });
    return () => { cancelled = true; };
  }, [account?.token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Retour de la boutique : les droits ont pu changer, on les relit en entrant
  // sur le profil plutôt que d'attendre un rechargement.
  useEffect(() => {
    if ((path !== ROUTES.profile && path !== ROUTES.settings) || !account?.token) return;
    fetch(`${API}/auth/me/profile`, { headers: { Authorization: `Bearer ${account.token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (!d) return;
        setAvatarRights(d.avatarUploads || { image: false, gif: false });
        setOwnedThemes(d.themes || []);
        setOwnedPalettes(d.palettes || []);
        // Au retour de la boutique, la parure vient peut-être de changer.
        setPlayer(p => (p ? { ...p, cosmetics: d.cosmetics || null } : p));
      })
      .catch(() => {});
  }, [path, account?.token]);

  // Première ouverture du jeu : l'installation du superadmin passe avant tout le
  // reste. En cas de serveur injoignable on n'immobilise pas l'appli — l'écran
  // normal s'affiche et les erreurs réseau parlent d'elles-mêmes.
  useEffect(() => {
    fetch(`${API}/admin/has-superadmin`)
      .then(r => r.json())
      .then(d => setNeedsSetup(!d.exists))
      .catch(() => setNeedsSetup(false));
  }, []);

  // ── Routing ────────────────────────────────────────────────────────────────
  // Redirects: an address whose preconditions are not met (no identity, no
  // account, unknown route) is replaced, never pushed.
  useEffect(() => {
    const known = [ROUTES.home, ROUTES.auth, ROUTES.profile, ROUTES.settings, ROUTES.stats, ROUTES.medals, ROUTES.shop, ROUTES.admin, ROUTES.system];
    if (!known.includes(path) && !routeGameId) {
      navigate(ROUTES.home, { replace: true });
      return;
    }
    // The shop and stats only make sense with an account
    if ((path === ROUTES.shop || path === ROUTES.stats || path === ROUTES.medals) && !account) {
      navigate(ROUTES.profile, { replace: true });
      return;
    }
    // Logged in but no in-game name yet -> profile setup. Tant que l'identite
    // du compte n'est pas revenue du serveur, on ne conclut rien : sinon un
    // simple rafraichissement renvoie vers le profil.
    if (path === ROUTES.home && !player.pseudo && account && !identityLoading) {
      navigate(ROUTES.profile, { replace: true });
    }
  }, [path, routeGameId, account, player.pseudo, identityLoading]);

  // Joining a game is triggered by the /partie/:code address, whether it comes
  // from an invite link, a typed code or a back navigation.
  useEffect(() => {
    if (!routeGameId || identityLoading) return;
    // Link typed in lowercase: align the address with the real code
    if (path !== ROUTES.game(routeGameId)) navigate(ROUTES.game(routeGameId), { replace: true });
    if (!player.pseudo) {
      // Invite link opened without an identity: ask for it, then join
      setPendingGameId(routeGameId);
      navigate(ROUTES.home, { replace: true });
      return;
    }
    if (gameId === routeGameId) return;          // already in this game
    if (joinAskedRef.current === routeGameId) return; // request already in flight
    joinAskedRef.current = routeGameId;
    socket.emit("joinGame", { gameId: routeGameId, player: playerPayload() });
  }, [path, routeGameId, player.pseudo, gameId, identityLoading]);

  // The account token follows the player: it is passed to the server when
  // creating/joining a game so stats are attached to the account
  useEffect(() => {
    setPlayer(p => (p && p.pseudo ? { ...p, token: account?.token || null } : p));
  }, [account]);

  // Retour du fournisseur SSO : le serveur renvoie ici avec le jeton dans le
  // fragment (jamais dans la query — il n'a alors rien à faire dans les
  // journaux). On l'échange contre l'identité du compte.
  const consumeSsoHash = (hash) => {
    if (!hash.startsWith("#sso_")) return;
    const params = new URLSearchParams(hash.slice(1));
    const token = params.get("sso_token");
    const ssoError = params.get("sso_error");
    if (ssoError) { alertDialog(t("auth.ssoError", { error: ssoError })); return; }
    if (!token) return;
    fetch(`${API}/auth/me/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(d => handleAuthSuccess({
        token, username: d.username, role: d.role,
        pseudo: d.pseudo || null, picture: d.picture || null,
      }, "login"))
      .catch(() => alertDialog(t("common.error")));
  };

  useEffect(() => {
    const hash = window.location.hash || "";
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    consumeSsoHash(hash);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dans l'appli : le retour SSO arrive par le schéma custom popuquiz://
  // (ouvert par le navigateur embarqué), pas par une navigation de page — on
  // écoute l'événement plutôt que de relire location.hash.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const sub = CapacitorApp.addListener("appUrlOpen", ({ url }) => {
      Browser.close().catch(() => {});
      const hashIdx = url.indexOf("#");
      if (hashIdx === -1) return;
      consumeSsoHash(url.slice(hashIdx));
    });
    return () => { sub.then(s => s.remove()); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One-time migration: the admin panel used to keep its own token ("adminToken").
  // Fold any legacy token into the unified account store, then drop the old key.
  useEffect(() => {
    const legacy = localStorage.getItem("adminToken");
    if (!legacy) return;
    if (!localStorage.getItem("popuquiz_account")) {
      try {
        const p = JSON.parse(atob(legacy.split(".")[1]));
        const acc = { token: legacy, username: p.username, role: p.role };
        localStorage.setItem("popuquiz_account", JSON.stringify(acc));
        setAccount(acc);
      } catch (_) {}
    }
    localStorage.removeItem("adminToken");
  }, []);

  // ── Avatar ─────────────────────────────────────────────────────────────────
  // Resize to 256px max before storing: the image is rebroadcast to every player
  // on each updatePlayers, and a full-size base64 bloats the sync.
  // La photo redimensionnée part ensuite sur le serveur : seule son URL circule
  // ensuite (localStorage, payload joueur, synchros). Si l'envoi échoue, on
  // retombe sur le base64 local — mieux vaut un avatar que pas de partie.
  const AVATAR_MAX = 256;
  // Garde-fou côté client, aligné sur la limite du serveur.
  const AVATAR_MAX_BYTES = 4 * 1024 * 1024;
  const uploadAvatar = (file) => {
    if (!file) return;

    // Un GIF ne survit pas au canevas : redimensionné, il ressort en image fixe.
    // Quand le droit est acquis, on l'envoie donc tel quel — l'animation est
    // précisément ce qui a été acheté.
    const isGif = /gif$/i.test(file.type || "");
    if (isGif) {
      if (!avatarRights.gif) return alertDialog(t("profile.gifLocked"));
      if (file.size > AVATAR_MAX_BYTES) return alertDialog(t("profile.gifTooBig", { mb: Math.round(AVATAR_MAX_BYTES / 1024 / 1024) }));
      const fd = new FormData();
      fd.append("avatar", file, "avatar.gif");
      fetch(`${API}/avatar`, {
        method: "POST", body: fd,
        headers: account?.token ? { Authorization: `Bearer ${account.token}` } : {},
      })
        .then(r => r.json().then(d => ({ ok: r.ok, d })))
        .then(({ ok, d }) => {
          if (!ok || !d.url) throw new Error(d.error || "upload");
          setPicture(d.url);
        })
        .catch(err => alertDialog(err.message || t("common.error")));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, AVATAR_MAX / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        const fallback = canvas.toDataURL("image/jpeg", 0.82);
        canvas.toBlob(async (blob) => {
          if (!blob) return setPicture(fallback);
          try {
            const fd = new FormData();
            fd.append("avatar", blob, "avatar.jpg");
            const res = await fetch(`${API}/avatar`, {
              method: "POST", body: fd,
              headers: account?.token ? { Authorization: `Bearer ${account.token}` } : {},
            });
            const data = await res.json();
            if (!res.ok || !data.url) throw new Error(data.error || "upload");
            setPicture(data.url);
          } catch (err) {
            // Refus du serveur (objet non acheté) : on n'invente pas une photo
            // locale qui ne suivrait sur aucun autre appareil.
            if (String(err.message || "").includes("boutique")) alertDialog(err.message);
            else setPicture(fallback);
          }
        }, "image/jpeg", 0.82);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  // ── Navigation between identity screens ────────────────────────────────────
  const openProfile = () => {
    // Reset the draft from the committed identity (edits can be cancelled)
    setName(player.pseudo || account?.username || "");
    setPicture(player.picture || "");
    navigate(ROUTES.profile);
  };
  const startAnonymousSetup = () => { setName(""); navigate(ROUTES.profile); };
  const openAuth = () => navigate(ROUTES.auth);

  // Single source of truth for the session: the account (token + role) lives in
  // one place, shared by the player and the admin panel — so logging out of one
  // logs out of the other.
  const persistAccount = (acc) => {
    localStorage.setItem("popuquiz_account", JSON.stringify(acc));
    setAccount(acc);
  };
  const clearAccount = () => {
    localStorage.removeItem("popuquiz_account");
    setAccount(null);
  };

  // Envoie l'identite de jeu au compte ouvert. Sans compte, ne fait rien.
  const pushIdentityToAccount = (token, pseudo, pic) => {
    if (!token || !pseudo) return Promise.resolve();
    return fetch(`${API}/auth/me/profile`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ pseudo, picture: pic || "" }),
    }).catch(() => {});
  };

  // Successful login / sign-up (mode = "login" | "register")
  const handleAuthSuccess = (acc, mode) => {
    persistAccount(acc);
    // Un compte est ouvert : le navigateur ne garde plus d'identite a lui.
    clearLocalIdentity();

    // Inscription : ce qui avait ete choisi en anonyme (nom, photo) devient
    // l'identite du tout nouveau compte, comme les pieces mises de cote.
    if (mode === "register") {
      const local = { pseudo: player.pseudo || name.trim(), picture: player.picture || picture };
      if (local.pseudo) {
        pushIdentityToAccount(acc.token, local.pseudo, local.picture);
        setName(local.pseudo);
        setPicture(local.picture || "");
        setPlayer({ pseudo: local.pseudo, picture: local.picture || "", role, playerId: getOrCreatePlayerId(), token: acc.token });
        navigate(pendingGameId ? ROUTES.game(pendingGameId) : ROUTES.home, { replace: true });
        setPendingGameId(null);
        return;
      }
      setName(n => n || acc.username);
      navigate(ROUTES.profile, { replace: true });
      return;
    }

    // Connexion : l'identite du compte remplace celle de ce navigateur, quelle
    // qu'elle soit. Un joueur connecte depuis un autre appareil ne doit jamais
    // voir son profil ecrase par un choix anonyme local.
    if (acc.pseudo) {
      setName(acc.pseudo);
      setPicture(acc.picture || "");
      setPlayer({ pseudo: acc.pseudo, picture: acc.picture || "", role, playerId: getOrCreatePlayerId(), token: acc.token });
      navigate(pendingGameId ? ROUTES.game(pendingGameId) : ROUTES.home, { replace: true });
      setPendingGameId(null);
      return;
    }
    // Compte sans identite enregistree : on la demande, en repartant de zero.
    setName(acc.username);
    setPicture("");
    setPlayer({});
    navigate(ROUTES.profile, { replace: true });
  };

  // Deconnexion : le compte et l'identite partent ensemble, et l'on revient a
  // l'ecran d'entree — il ne reste rien de la session precedente.
  const handleLogout = () => {
    clearAccount();
    clearLocalIdentity();
    localStorage.removeItem("popuquiz_gameId");
    currentIdentity = { pseudo: "", picture: "" };
    setName("");
    setPicture("");
    setAvatarRights({ image: false, gif: false });
    setOwnedThemes([]);
    setOwnedPalettes([]);
    setPlayer({});
    resetGameState();
    navigate(ROUTES.home, { replace: true });
  };

  // Save the identity (avatar + in-game name) and enter/return to the game.
  const saveProfile = () => {
    const finalName = name.trim();
    if (!finalName) return;
    writeLocalIdentity({ pseudo: finalName, picture }, !!account);
    currentIdentity = { pseudo: finalName, picture };
    setPlayer({ pseudo: finalName, picture, role, playerId: getOrCreatePlayerId(), token: account?.token || null });
    // Premier réglage du profil après une inscription : l'effet de la page
    // profil ne s'en charge pas (il attend un `player.pseudo` déjà posé), donc
    // c'est ici que l'identité rejoint le compte pour la première fois.
    pushIdentityToAccount(account?.token, finalName, picture);
    navigate(pendingGameId ? ROUTES.game(pendingGameId) : ROUTES.home, { replace: true });
    setPendingGameId(null);
  };

  const handleValidate = (questionId, playerId, valid, category) => {
    socket.emit("validateAnswer", { gameId, questionId, playerId, valid, category });
  };
  const handleStopGame = () => socket.emit("stopGame", { gameId });
  // Fermeture du salon depuis l'accueil ou le profil : c'est définitif pour
  // tout le monde, on demande confirmation.
  // Smash or Pass et Références en détente se mettent en pause : en partant,
  // on propose d'enregistrer la partie pour la reprendre plus tard.
  const canPause = started && (gameMode === "smashpass" || (gameMode === "references" && rfMode === "detente"));
  const savePaused = () => new Promise(resolve => {
    socket.timeout(5000).emit("saveGame", { gameId }, (err, res) => resolve(!err && res?.ok));
  });

  const handleCloseLobby = async () => {
    if (canPause) {
      const choice = await choiceDialog(t("game.pauseCloseAsk"), [
        { value: "save", label: t("game.pauseSaveClose"), primary: true },
        { value: "close", label: t("game.pauseCloseOnly"), danger: true },
      ]);
      if (!choice) return;
      if (choice === "save" && !(await savePaused())) return alertDialog(t("game.pauseFailed"));
      return handleStopGame();
    }
    if (await confirmDialog(t("profile.confirmCloseLobby"))) handleStopGame();
  };
  // Retour au salon pour soi seul : la partie continue pour les autres.
  const handleReturnToLobby = async () => {
    if (await confirmDialog(t("profile.confirmReturnToLobby"))) socket.emit("returnToLobby", { gameId });
  };
  const handleLeaveGame = () => socket.emit("leaveGame", { gameId });

  // End of game: restart as-is, go back to the lobby, or leave
  const handleReplayGame = () => socket.emit("replayGame", { gameId });
  const handleBackToLobby = () => socket.emit("backToLobby", { gameId });
  const handleQuitFromScores = () => handleLeaveGame();

  const handleLeaveGameFromProfile = async () => {
    if (canPause) {
      // Chacun pour soi : enregistrer ne ferme pas la partie des autres.
      const choice = await choiceDialog(t("game.pauseLeaveAsk"), [
        { value: "save", label: t("game.pauseSaveLeave"), primary: true },
        { value: "leave", label: t("game.pauseLeaveOnly") },
      ]);
      if (!choice) return;
      if (choice === "save" && !(await savePaused())) return alertDialog(t("game.pauseFailed"));
    } else if (!(await confirmDialog(t("game.confirmLeave")))) return;
    handleLeaveGame();
    navigate(ROUTES.home, { replace: true });
  };

  // Home: create or join a game
  const handleCreateGame = () => socket.emit("createGame", { player });
  const handleJoinGame = (code) => {
    const clean = (code || "").trim().toUpperCase();
    if (clean) navigate(ROUTES.game(clean));
  };

  const hasOngoingGame = !!gameId;

  // Parure changée depuis le profil : la barre du haut la montre aussitôt, et
  // en pleine partie le serveur la relit pour que les autres la voient aussi.
  const updateCosmetics = (cosmetics) => {
    setPlayer(p => (p ? { ...p, cosmetics } : p));
    if (gameId) socket.emit("updateIdentity", { gameId });
  };

  // On the profile page there is no "Save" button: the in-game name and the
  // avatar are persisted on the fly (localStorage + player identity).
  // Except during initial setup (no nickname committed yet), where the identity
  // is only saved by the "Play" button.
  useEffect(() => {
    if (path !== ROUTES.profile || !player.pseudo) return;
    const finalName = name.trim();
    if (!finalName) return; // never overwrite the identity with an empty name
    writeLocalIdentity({ pseudo: finalName, picture }, !!account);
    setPlayer(p => ({ ...(p || {}), pseudo: finalName, picture, role, playerId: getOrCreatePlayerId(), token: account?.token || null }));

    // Compte connecté : l'identité rejoint le serveur pour suivre sur les
    // autres appareils. On laisse retomber la frappe avant d'envoyer, plutôt
    // qu'une requête par caractère.
    const accountToken = account?.token;
    const timers = [];
    if (accountToken) {
      timers.push(setTimeout(() => pushIdentityToAccount(accountToken, finalName, picture), 500));
    }
    // Retouche du profil pendant une partie : les autres joueurs voient encore
    // l'ancienne identité tant que le serveur ne l'a pas apprise.
    if (gameId) {
      timers.push(setTimeout(() => socket.emit("updateIdentity", { gameId, pseudo: finalName, picture }), 400));
    }
    return () => timers.forEach(clearTimeout);
  }, [name, picture, path, gameId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Shared props for the navigation bar (including the profile dropdown)
  const navProps = {
    player,
    account,
    hasOngoingGame,
    inGame: started,
    isHost: role === "host",
    isAdmin: ["admin", "superadmin"].includes(account?.role),
    isSuperAdmin: account?.role === "superadmin",
    onAdminClick: () => navigate(ROUTES.admin),
    onSystemClick: () => navigate(ROUTES.system),
    onCloseLobby: handleCloseLobby,
    onProfileClick: openProfile,
    onSettingsClick: () => navigate(ROUTES.settings),
    // Boutique fermée : plus de bouton nulle part.
    onShopClick: features.shop ? () => navigate(ROUTES.shop) : undefined,
    onLogout: handleLogout,
    onLeaveGame: handleLeaveGameFromProfile,
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  // Instance jamais installée : l'écran de création du superadmin passe avant
  // l'accueil, la connexion et l'admin — c'est la première chose qu'on voit.
  // Compte ouvert dont l'identite n'est pas encore revenue du serveur : on
  // attend plutot que de montrer l'ecran d'entree, qui clignoterait.
  if (needsSetup === null || identityLoading) {
    return <div class="container"><div class="Home"><Loading full /></div></div>;
  }
  if (needsSetup) {
    return (
      <Setup
        onDone={(acc) => {
          persistAccount(acc);
          setNeedsSetup(false);
          setName(n => n || acc.username);
          navigate(ROUTES.profile, { replace: true });
        }}
      />
    );
  }

  // Admin: its own world (reachable from home or the lobby)
  // Deux portes vers le meme panneau : la questiotheque (les questions) et
  // l'administration (les reglages du serveur). Chacune n'ouvre que sa moitie.
  if (path === ROUTES.admin || path === ROUTES.system) {
    return (
      <div class="container">
        {/* La clé remonte le panneau quand on passe de l'administration à la
            questiothèque (lien du tableau de bord) : chaque moitié repart de sa vue. */}
        <Admin key={path}
          section={path === ROUTES.system ? "system" : "library"}
          onBack={() => goBack(ROUTES.home)}
          account={account}
          onAuthenticated={persistAccount}
          onSessionExpired={clearAccount}
          navProps={navProps}
        />
      </div>
    );
  }

  if (path === ROUTES.auth) {
    return (
      <Auth
        initialMode={authMode}
        onSuccess={handleAuthSuccess}
        onBack={() => goBack(ROUTES.home)}
      />
    );
  }

  if (path === ROUTES.shop && account && features.shop) {
    return (
      <div class="container">
        <Navigation {...navProps} />
        <Shop account={account} onExpired={handleLogout} onBack={() => goBack(ROUTES.profile)} />
      </div>
    );
  }

  if (path === ROUTES.settings) {
    return (
      <Settings
        player={player}
        account={account}
        hasOngoingGame={hasOngoingGame}
        inGame={started}
        isHost={role === "host"}
        onProfileClick={openProfile}
        onShopClick={features.shop ? () => navigate(ROUTES.shop) : undefined}
        ownedThemes={ownedThemes}
        ownedPalettes={ownedPalettes}
        isAdmin={["admin", "superadmin"].includes(account?.role)}
        isSuperAdmin={account?.role === "superadmin"}
        onAdminClick={() => navigate(ROUTES.admin)}
        onSystemClick={() => navigate(ROUTES.system)}
        onLogout={handleLogout}
        onLeaveGame={handleLeaveGameFromProfile}
        onCloseLobby={handleCloseLobby}
        onBack={() => goBack(ROUTES.home)}
      />
    );
  }

  if (path === ROUTES.stats && account) {
    return (
      <div class="container">
        <Navigation {...navProps} />
        <PlayerStats account={account} picture={picture} onExpired={handleLogout} onBack={() => goBack(ROUTES.profile)} />
      </div>
    );
  }

  if (path === ROUTES.medals && account) {
    return (
      <div class="container">
        <Navigation {...navProps} />
        <MedalsPage account={account} onExpired={handleLogout} onBack={() => goBack(ROUTES.profile)} />
      </div>
    );
  }

  // Profile: initial setup as long as no nickname has been committed
  if (path === ROUTES.profile) {
    const isSetup = !player.pseudo;
    return (
      <Profile
        variant={isSetup ? "setup" : "profile"}
        account={account}
        name={name}
        picture={picture}
        cosmetics={player.cosmetics}
        onCosmetics={updateCosmetics}
        onNameChange={setName}
        onUploadAvatar={uploadAvatar}
        avatarRights={avatarRights}
        canChangePassword={hasPassword}
        onSave={saveProfile}
        onBack={isSetup && account ? undefined : () => goBack(ROUTES.home)}
        onLogin={openAuth}
        onLogout={handleLogout}
        hasOngoingGame={hasOngoingGame}
        onLeaveGame={handleLeaveGameFromProfile}
        onStatsExpired={handleLogout}
        onOpenShop={features.shop ? () => navigate(ROUTES.shop) : undefined}
        onOpenSettings={() => navigate(ROUTES.settings)}
        onOpenFullStats={() => navigate(ROUTES.stats)}
        onOpenFullMedals={() => navigate(ROUTES.medals)}
        onProfileClick={openProfile}
        inGame={started}
        isHost={role === "host"}
        isAdmin={["admin", "superadmin"].includes(account?.role)}
        onAdminClick={() => navigate(ROUTES.admin)}
        onReturnToLobby={handleReturnToLobby}
        onCloseLobby={handleCloseLobby}
      />
    );
  }

  // ── Game screen: /partie/:code ─────────────────────────────────────────────
  if (routeGameId) {
    if (gameId !== routeGameId) {
      return (
        <div class="container">
          <Navigation {...navProps} />
          <div class="Home"><Loading full label={t("game.connecting", { code: routeGameId })} /></div>
        </div>
      );
    }

    if (started) {
      return (
        <div class="container">
          <Navigation {...navProps} />
          {gameMode === "blindtest"
            ? (btVariant === "paroles"
                ? <ParolesGame gameId={gameId} player={player} />
                : btVariant === "cacophonie"
                ? <CacophonieBlindTest gameId={gameId} player={player} />
                : <BlindTest gameId={gameId} player={player} variant={btVariant} />)
            : gameMode === "imposteur"
            ? <ImposterQuiz gameId={gameId} player={player} />
            : gameMode === "battleroyale"
            ? <BattleRoyale gameId={gameId} player={player} />
            : gameMode === "buzzer"
            ? <BuzzerQuiz gameId={gameId} player={player} />
            : gameMode === "bombe"
            ? <BombGame gameId={gameId} player={player} />
            : gameMode === "pixelguess"
            ? <PixelGuess gameId={gameId} player={player} />
            : gameMode === "references"
            ? <References gameId={gameId} player={player} />
            : gameMode === "smashpass"
            ? <SmashPass gameId={gameId} player={player} />
            : gameMode === "pictionary"
            ? <PictionaryGame gameId={gameId} player={player} />
            : gameMode === "hemicycle"
            ? <HemicycleGame gameId={gameId} player={player} />
            : gameMode === "undercover"
            ? <UndercoverGame gameId={gameId} player={player} />
            : <Quiz gameId={gameId} />}
        </div>
      );
    }

    if (tieBreak) {
      return (
        <div class="container">
          <Navigation {...navProps} />
          <TieBreak gameId={gameId} role={role} data={tieBreak} />
        </div>
      );
    }

    if (showResults) {
      return (
        <div class="container">
          <Navigation {...navProps} />
          <Results
            role={role}
            results={results}
            answers={answers}
            questions={questions}
            players={players}
            validationView={validationView}
            onValidate={handleValidate}
            gameId={gameId}
            imposterRoles={imposterRoles}
          />
        </div>
      );
    }

    if (showScores && results.players) {
      return (
        <div class="container">
          <Navigation {...navProps} />
          <FinalScores
            results={results}
            player={player}
            onSignup={() => { setAuthMode("register"); navigate(ROUTES.auth); }}
            isHost={role === "host"}
            onReplay={handleReplayGame}
            onReconfigure={handleBackToLobby}
            onQuit={handleQuitFromScores}
          />
        </div>
      );
    }

    return (
      <div class="container">
        <Navigation {...navProps} />
        <GameLobby player={player} players={players} gameId={gameId} onStop={handleStopGame} onLeave={handleLeaveGame} />
      </div>
    );
  }

  // ── Home ───────────────────────────────────────────────────────────────────
  if (!player.pseudo) {
    return (
      <Welcome
        onLogin={openAuth}
        onAnonymous={startAnonymousSetup}
      />
    );
  }

  return (
    <div class="container">
      <Navigation {...navProps} />
      <Lobby
        player={player}
        ongoingGameId={gameId}
        inGame={started}
        isHost={role === "host"}
        onResume={() => navigate(ROUTES.game(gameId))}
        onCreate={handleCreateGame}
        onJoin={handleJoinGame}
        onCloseLobby={handleCloseLobby}
        onResumeSaved={(saveId) => socket.emit("resumeSaved", { saveId, player })}
        onLeave={handleLeaveGameFromProfile}
      />
    </div>
  );
}

export default App;
